import React, { memo, useCallback, useEffect, useState } from "react"
import { AspectRatio, Box, Flex, Image, Text, useBreakpointValue } from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Pagination } from "swiper"

import { ProductCard } from "@app/components/Product/Card/ProductCard"
import { useShopify } from "@app/hooks/useShopify"
import { useImage } from "@app/hooks/useImage"

import type { ProductProps } from "@root/types/global"
import "swiper/css"
import { useRoutes } from "@app/hooks/useRoutes"
import { Link } from "@app/components/Link"

type Props = {
  curations: GatsbyTypes.SanityProductCurations
  product: ProductProps
}

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Pagination])

export const ProductCurations: React.FC<Props> = memo(({ curations, product }) => {
  const { getHandle, getProducts } = useShopify()
  const { getGatsbyImage } = useImage()
  const { urlResolver } = useRoutes()
  const { imagePretitle, imageTitle, sectionDescription } = curations
  const [items, setItems] = useState<Array<ProductProps> | null>(null)
  const image = getGatsbyImage(curations?.image)
  const link = urlResolver(curations?.link)
  const ratio = useBreakpointValue({ base: 0.535, md: 0.6 })

  const fetchItems = useCallback(async () => {
    const items = await getProducts({ firstImages: 2, firstVariants: 1, handles: curations?.products?.map(item => getHandle(item)) })

    if (items) setItems(items)
  }, [curations, getHandle, getProducts])

  // intentionally only run once at first render
  useEffect(() => {
    if (!items && curations?.products?.length) fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!curations?.products || !items) return null

  return (
    <Box w="100%" bgColor="grey.cloud" paddingTop={{ base: 8, md: 14 }} paddingBottom={14} flexDirection={{ base: "column", md: "row" }}>
      {!image && sectionDescription && (
        <Box display={{ base: "block", md: "none" }} width="100%" maxWidth="544px" paddingLeft="4" paddingRight="4" marginBottom="6">
          {imagePretitle && (
            <Text size="xs" fontWeight="500" mb={{ base: "2", md: "1" }} lineHeight="20px" textTransform="uppercase" color="grey.text">
              {imagePretitle}
            </Text>
          )}

          <Text
            fontSize={{ base: "20px", sm: "28px" }}
            fontWeight="500"
            lineHeight={{ base: "24px", md: "32px" }}
            mb={{ base: "4", md: "6" }}
            color="grey.text"
          >
            {sectionDescription}
          </Text>

          {link && (
            <Text
              as={Link}
              to={link.url}
              title={link.title}
              aria-label={link.title}
              textDecoration="underline"
              size="sm"
              textUnderlineOffset="4px"
              fontWeight="500"
              color="grey.text"
            >
              {link.title}
            </Text>
          )}
        </Box>
      )}
      <Flex
        position="relative"
        sx={{
          ".swiper": {
            width: "100%",
          },
          ".swiper-slide": {
            height: "unset !important",
          },
          ".swiper-pagination-progressbar .swiper-pagination-progressbar-fill": {
            bg: "grey.coal",
          },
          ".swiper-pagination-progressbar": {
            bg: "grey.mid",
            height: "2px !important",
            marginX: { base: "16px", sm: "56px" },
            width: { base: "calc(100% - 32px)", sm: "calc(100% - 112px)" },
          },
        }}
      >
        {!image && sectionDescription && (
          <Box display={{ base: "none", md: "block" }} width="100%" maxWidth="544px" paddingLeft="14" paddingRight="32">
            {imagePretitle && (
              <Text size="xs" fontWeight="500" mb="1" lineHeight="20px" textTransform="uppercase" color="grey.text">
                {imagePretitle}
              </Text>
            )}

            <Text fontSize={{ base: "20px", sm: "28px" }} fontWeight="500" lineHeight="32px" mb="4" color="grey.text">
              {sectionDescription}
            </Text>

            {link && (
              <Text
                as={Link}
                to={link.url}
                title={link.title}
                aria-label={link.title}
                textDecoration="underline"
                size="sm"
                textUnderlineOffset="4px"
                fontWeight="500"
                color="grey.text"
              >
                {link.title}
              </Text>
            )}
          </Box>
        )}
        <Swiper
          autoHeight={true}
          pagination={{ el: `#curation-${product?.handle}-progress`, type: "progressbar" }}
          navigation={{ prevEl: `#curation-${product?.handle}-navigation-prev`, nextEl: `#curation-${product?.handle}-navigation-next` }}
          spaceBetween={16}
          breakpoints={{
            320: { slidesPerView: 1.5, slidesOffsetBefore: 16, slidesOffsetAfter: 16 },
            768: { slidesPerView: image ? 4.3 : 2.5, slidesOffsetBefore: image ? 56 : 0, slidesOffsetAfter: 56 },
          }}
        >
          {image && (
            <SwiperSlide>
              <Box position="relative">
                <AspectRatio ratio={ratio}>
                  <Image {...image} />
                </AspectRatio>
                <Box position="absolute" top="0" left="0" padding="4" width="100%">
                  {imagePretitle && (
                    <Text size="xs" fontWeight="500" mb="2" textTransform="uppercase" color="grey.text">
                      {imagePretitle}
                    </Text>
                  )}
                  {imageTitle && (
                    <Text
                      fontSize={{ base: "20px", sm: "28px" }}
                      fontWeight="500"
                      lineHeight={{ base: "24px", md: "32px" }}
                      mb="4"
                      color="grey.text"
                    >
                      {imageTitle}
                    </Text>
                  )}
                  {link && (
                    <Text
                      as={Link}
                      to={link.url}
                      title={link.title}
                      aria-label={link.title}
                      textDecoration="underline"
                      size="sm"
                      textUnderlineOffset="4px"
                      fontWeight="500"
                      color="grey.text"
                    >
                      {link.title}
                    </Text>
                  )}
                </Box>
              </Box>
            </SwiperSlide>
          )}
          {items.map((curation, index) => (
            <SwiperSlide key={index}>
              <ProductCard item={curation} pagePosition={index} disableSwipe={true} />
            </SwiperSlide>
          ))}
        </Swiper>
        <Box
          id={`curation-${product?.handle}-progress`}
          pb={0}
          top="unset !important"
          bottom="-30px"
          sx={{
            ".swiper-pagination-progressbar-fill": {
              bg: "grey.coal",
            },
          }}
        />
      </Flex>
    </Box>
  )
})
